import { useEffect, useRef } from 'react'
import { useJoinFunnelAnalytics } from '@components/Analytics/useJoinFunnelAnalytics'
import { useReadableMembershipInfo } from '@components/TownPageLayout/useReadableMembershipInfo'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { publicPageLoginStore } from 'routes/PublicTownPage/usePublicPageLoginFlow'
import { useContractSpaceInfoWithoutClient } from 'use-towns-client'

import { getCachedSpaceDataForAnalytics } from './useGatherSpaceDetailsAnalytics'

const fiveMinutesInMs = 5 * 60 * 1000

export function usePublicTownsPageAnalyticsEvent({
    authenticated,
    overrideSpaceId,
}: {
    authenticated: boolean
    overrideSpaceId?: string | undefined
}) {
    const spaceIdFromPathname = useSpaceIdFromPathname()
    const spaceId = overrideSpaceId ?? spaceIdFromPathname
    const { data: membershipInfo } = useReadableMembershipInfo(spaceId ?? '')
    const { data: spaceInfo } = useContractSpaceInfoWithoutClient(spaceId)
    const { publicTownPage } = useJoinFunnelAnalytics()

    const lastFiredTimeRef = useRef<Record<string, number>>({})
    const lastSpaceIdRef = useRef<string | null>(null)

    useEffect(() => {
        const { gatedSpace, pricingModule } = getCachedSpaceDataForAnalytics()
        if (spaceInfo && membershipInfo && pricingModule && gatedSpace !== undefined && spaceId) {
            const now = Date.now()
            const spaceIdChanged = lastSpaceIdRef.current !== spaceId
            const lastFiredForSpace = lastFiredTimeRef.current[spaceId] || 0
            const timeSinceLastFired = now - lastFiredForSpace

            // only fire if spaceId changed OR it's been more than 5 minutes since last firing for this spaceId
            if (spaceIdChanged || timeSinceLastFired >= fiveMinutesInMs) {
                publicTownPage({
                    authenticated,
                    spaceId,
                    spaceName: spaceInfo.name,
                    pricingModel: membershipInfo.remainingFreeSupply ? 'free' : 'paid',
                    postOAuthRedirect: !!publicPageLoginStore.getState().spaceBeingJoined,
                    gatedSpace,
                    pricingModule,
                })

                lastFiredTimeRef.current[spaceId] = now
                lastSpaceIdRef.current = spaceId
            }
        }
    }, [spaceInfo, membershipInfo, spaceId, authenticated, publicTownPage])
}
